import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    IconButton,
    List,
    ListItem,
    makeStyles,
    Menu,
    MenuItem,
    Typography,
} from "@material-ui/core"
import { Add, DragIndicator, ExpandMore, MoreVert } from "@material-ui/icons"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { useSnackbar } from "../../../utils/notificacoes"
import { passoCasaBusca } from "../utils/buscaCardapio"
import { useCardapioContext } from "./CardapioContext"
import DialogConfirmacao from "./DialogConfirmacao"
import ItemAdicional from "./ItemAdicional"
import { destacarTermo } from "./TextoEditavel"

const ITEM_HEIGHT = 48

const useStyles = makeStyles(theme => ({
    container: {
        width: "100%",
        // Compactação vertical: remove a folga extra que o Accordion do MUI
        // adiciona quando expandido e reduz a altura do cabeçalho e dos detalhes.
        // Usa só marginTop/marginBottom para NÃO zerar o margin-left/right auto
        // (a centralização do passo), senão ele "cola" na lateral ao expandir.
        "&.Mui-expanded": { marginTop: 4, marginBottom: 4 },
        "& .MuiAccordionSummary-root": { minHeight: 44 },
        "& .MuiAccordionSummary-root.Mui-expanded": { minHeight: 44 },
        "& .MuiAccordionSummary-content": { margin: "6px 0", alignItems: "center" },
        "& .MuiAccordionSummary-content.Mui-expanded": { margin: "6px 0" },
        "& .MuiAccordionDetails-root": { padding: "0 12px 8px" },
        // Indentação por nível (desktop): Passo fica ~89% do produto (~80% da categoria).
        [theme.breakpoints.up("md")]: {
            width: "89%",
            marginLeft: "auto",
            marginRight: "auto",
            // Ao expandir, o MUI aplica `margin: 16px 0` (zera as laterais e
            // descentraliza). Reforçamos left/right auto no próprio estado
            // expandido, com a mesma especificidade, para o passo manter a
            // exata posição — igual fechado e aberto.
            "&.Mui-expanded": {
                marginTop: 4,
                marginBottom: 4,
                marginLeft: "auto",
                marginRight: "auto",
            },
        },
    },
    // Destaque enquanto o passo (grupo de opções) está sendo arrastado.
    containerArrastando: {
        boxShadow: "0 0 0 2px rgba(var(--cor-botao-salvar-rgb),0.5)",
        backgroundColor: "#e8f5e9",
        opacity: 0.9,
    },
    lista: {
        width: "100%",
        paddingTop: 0,
        paddingBottom: 0,
    },
    // Cabeçalho unificado do grupo: nome + contagem à esquerda; ações à direita.
    tituloGrupo: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 6,
        overflow: "hidden",
    },
    nomeGrupo: {
        fontWeight: 600,
        color: "#2c3e50",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    contagemItens: {
        fontSize: "0.78rem",
        color: "rgba(0,0,0,0.5)",
        whiteSpace: "nowrap",
    },
    botaoAdicionarItem: {
        textTransform: "none",
        fontWeight: 600,
        color: "#2e7d32",
        whiteSpace: "nowrap",
        padding: "2px 8px",
        "&:hover": { backgroundColor: "rgba(var(--cor-botao-salvar-rgb),0.08)" },
    },
    botaoSalvar: {
        backgroundColor: "#14A44D",
        color: "white",
    },
    // dragIndicator: {
    //     cursor: "move",
    // },
}))

const ItemPasso = memo(function ItemPasso({
    opcao,
    conteudo,
    termoBusca,
    dragHandleProps,
    arrastando,
}) {
    const {
        setCadastrar,
        setModalPasso,
        setOpenPassoDeMontagem,
        setProdutoAtual,
        handleExcluirGrupoDeOpcao,
        handleToggleDuplicarGrupoDeMontagem,
        handleCadastrarOpcao,
        setCategorias,
        handleIdsEditar,
    } = useCardapioContext()

    const { enqueueSnackbar } = useSnackbar()
    const alertStart = (msg, tipo) => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const [anchorEl, setAnchorEl] = useState(null)
    const [confirmarExcluir, setConfirmarExcluir] = useState(false)
    const openMoreItemDeMontagem = Boolean(anchorEl)

    const handleClickMoreItemDeMontagem = useCallback(e => {
        setAnchorEl(e.currentTarget)
        e.stopPropagation()
    }, [])

    const handleCloseMoreItemDeMontagem = useCallback(e => {
        setAnchorEl(null)
        e.stopPropagation()
    }, [])

    const classes = useStyles()

    const handleClickPassoDeMontagem = useCallback(() => {
        setOpenPassoDeMontagem(true)
    }, [setOpenPassoDeMontagem])

    // const handleDragStart = index => {
    //     setDraggingOpcoes(index)
    // }
    // const handleDragEnd = () => {
    //     setDraggingOpcoes(null)
    // }

    const [expanded, setExpanded] = useState(false)

    const handleExpansion = useCallback(() => {
        setExpanded(prevExpanded => !prevExpanded)
    }, [])

    // Abre o passo automaticamente quando a busca casa pelo nome dele ou por
    // alguma opção dentro dele, para o resultado aparecer já visível.
    const termo = (termoBusca ?? "").toLowerCase()
    const buscaAbrePasso = passoCasaBusca(opcao, termo)
    const aberto = expanded || buscaAbrePasso
    // Mostra todos os itens do passo quando: não há busca; o nome do passo casa
    // com o termo; o nome do produto casa (o produto inteiro é o resultado); ou
    // o usuário abriu o passo manualmente (expanded). Fora esses casos, durante
    // a busca só ficam visíveis os itens que casam (ex.: "morango").
    const passoNomeCasa = !!termo && (opcao?.nome ?? "").toLowerCase().includes(termo)
    const produtoNomeCasa = !!termo && (conteudo?.nome ?? "").toLowerCase().includes(termo)
    const mostrarTodosItens = !termo || passoNomeCasa || produtoNomeCasa || expanded

    const opcoesOrdenados = useMemo(() => {
        if (!aberto) return []
        const o = opcao?.opcoes ?? []
        return [...o].sort((a, b) => a.ordemExib - b.ordemExib)
    }, [opcao?.opcoes, aberto])

    // Reordena as opções por arrastar (igual ao produto). Ao mover, reatribui
    // `ordemExib` na ordem visível e marca o produto como editado (persistido no
    // "Salvar" global). Só o passo/produto afetado é reconstruído imutavelmente.
    const draggingIndexRef = useRef(null)
    const [itemArrastadoId, setItemArrastadoId] = useState(null)

    const aplicarNovaOrdem = useCallback(
        (deIndex, paraIndexDesejado) => {
            if (deIndex === null || deIndex === undefined) return
            const max = opcoesOrdenados.length - 1
            const paraIndex = Math.max(0, Math.min(max, paraIndexDesejado))
            if (deIndex === paraIndex) return
            const arr = [...opcoesOrdenados]
            const [movido] = arr.splice(deIndex, 1)
            arr.splice(paraIndex, 0, movido)
            const reordenados = arr.map((it, idx) => ({ ...it, ordemExib: idx }))
            handleIdsEditar(conteudo.id)
            const visiveisIds = new Set(reordenados.map(it => it.id))
            setCategorias(prevCats =>
                prevCats?.map(cat => {
                    if (!cat.produtos?.some(prod => prod.id === conteudo.id)) return cat
                    return {
                        ...cat,
                        produtos: cat.produtos.map(prod =>
                            prod.id !== conteudo.id
                                ? prod
                                : {
                                      ...prod,
                                      opcoes: prod.opcoes?.map(passo =>
                                          passo.id !== opcao.id
                                              ? passo
                                              : {
                                                    ...passo,
                                                    opcoes: [
                                                        ...reordenados,
                                                        ...(passo.opcoes ?? []).filter(
                                                            it => !visiveisIds.has(it.id)
                                                        ),
                                                    ],
                                                }
                                      ),
                                  }
                        ),
                    }
                })
            )
            return paraIndex
        },
        [opcoesOrdenados, conteudo.id, opcao.id, setCategorias, handleIdsEditar]
    )

    const handleReordenarOpcao = useCallback(
        overIndex => {
            const from = draggingIndexRef.current
            if (from === null || from === overIndex) return
            aplicarNovaOrdem(from, overIndex)
            draggingIndexRef.current = overIndex
        },
        [aplicarNovaOrdem]
    )

    const handleDefinirOrdemItem = useCallback(
        (itemIndex, novoIndiceDesejado) => {
            aplicarNovaOrdem(itemIndex, novoIndiceDesejado)
        },
        [aplicarNovaOrdem]
    )

    const handleClickAdicionarItem = useCallback(() => {
        handleCadastrarOpcao(conteudo?.id, opcao?.id)
    }, [handleCadastrarOpcao, conteudo?.id, opcao?.id])

    // Contagem de itens do grupo, exibida ao lado do nome no cabeçalho unificado.
    const totalItens = (opcao?.opcoes ?? []).length

    return (
        <Accordion
            className={`${classes.container} ${arrastando ? classes.containerArrastando : ""}`}
            expanded={aberto}
            onChange={handleExpansion}
        >
            <AccordionSummary expandIcon={<ExpandMore />}>
                <IconButton
                    {...dragHandleProps}
                    size="small"
                    title="Arraste para reordenar"
                    aria-label="Arraste para reordenar"
                    onClick={e => e.stopPropagation()}
                    style={{ cursor: "grab" }}
                >
                    <DragIndicator fontSize="small" />
                </IconButton>
                <div className={classes.tituloGrupo}>
                    <Typography component="span" className={classes.nomeGrupo}>
                        {destacarTermo(opcao?.nome, termo)}
                    </Typography>
                    <Typography component="span" className={classes.contagemItens}>
                        {`· ${totalItens} ${totalItens === 1 ? "item" : "itens"}`}
                    </Typography>
                </div>
                <Button
                    size="small"
                    className={classes.botaoAdicionarItem}
                    startIcon={<Add fontSize="small" />}
                    onClick={e => {
                        e.stopPropagation()
                        handleClickAdicionarItem()
                    }}
                >
                    Adicionar item
                </Button>
                <IconButton onClick={handleClickMoreItemDeMontagem}>
                    <MoreVert fontSize="small" />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={openMoreItemDeMontagem}
                    onClose={handleCloseMoreItemDeMontagem}
                    PaperProps={{
                        style: {
                            maxHeight: ITEM_HEIGHT * 4.5,
                            width: "20ch",
                        },
                    }}
                >
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation()
                            handleClickPassoDeMontagem()
                            setCadastrar(false)
                            setModalPasso(opcao)
                            setProdutoAtual(conteudo)
                        }}
                    >
                        Editar Passo
                    </MenuItem>
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation()
                            handleToggleDuplicarGrupoDeMontagem(opcao.id)
                        }}
                    >
                        Duplicar Passo
                    </MenuItem>
                    <MenuItem
                        onClick={e => {
                            e.stopPropagation()
                            setAnchorEl(null)
                            setConfirmarExcluir(true)
                        }}
                    >
                        Excluir Passo
                    </MenuItem>
                </Menu>
                <DialogConfirmacao
                    open={confirmarExcluir}
                    titulo="Excluir passo"
                    mensagem="Tem certeza de que deseja excluir o passo de montagem"
                    nomeItem={opcao?.nome}
                    onCancelar={() => setConfirmarExcluir(false)}
                    onConfirmar={() => {
                        setConfirmarExcluir(false)
                        handleExcluirGrupoDeOpcao(conteudo?.id, opcao?.id)
                    }}
                />
            </AccordionSummary>
            {aberto && (
                <AccordionDetails>
                    <List className={classes.lista}>
                        {opcoesOrdenados.map((item, itemIndex) => {
                            // Esconde os itens que não casam com o termo, exceto quando
                            // devemos mostrar todos (ver mostrarTodosItens). Índice
                            // preservado (reordenação intacta).
                            if (
                                !mostrarTodosItens &&
                                !(item?.nome ?? "").toLowerCase().includes(termo)
                            )
                                return null
                            return (
                                <ListItem
                                    key={item?.id}
                                    divider
                                    disableGutters
                                    onDragOver={e => {
                                        e.preventDefault()
                                        handleReordenarOpcao(itemIndex)
                                    }}
                                    onDrop={() => {
                                        draggingIndexRef.current = null
                                        setItemArrastadoId(null)
                                    }}
                                    onDragEnd={() => {
                                        draggingIndexRef.current = null
                                        setItemArrastadoId(null)
                                    }}
                                >
                                    <ItemAdicional
                                        produto={conteudo}
                                        passo={opcao}
                                        item={item}
                                        termoBusca={termoBusca}
                                        arrastando={item.id === itemArrastadoId}
                                        onDefinirOrdem={novoIndice =>
                                            handleDefinirOrdemItem(itemIndex, novoIndice)
                                        }
                                        dragHandleProps={{
                                            draggable: true,
                                            onDragStart: () => {
                                                draggingIndexRef.current = itemIndex
                                                setItemArrastadoId(item.id)
                                            },
                                        }}
                                    />
                                </ListItem>
                            )
                        })}
                    </List>
                </AccordionDetails>
            )}
        </Accordion>
    )
})

export default ItemPasso
